import React, { useContext, useEffect, useState, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Layers, CheckCircle2, Clock, AlertCircle, TrendingUp, 
  AlertTriangle, RefreshCw, BarChart3, Target, CheckCircle, Activity, MoreVertical, User, Filter, X, Trophy
} from 'lucide-react';
import CountUp from 'react-countup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useTaskDrawer } from '../hooks/useTaskDrawer';
import { TaskDrawer } from '../components/TaskDrawer';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { DateRangeFilter } from '../components/DateRangeFilter';
import BadgeAnnouncement from '../components/BadgeAnnouncement';
import BadgeWinnersStrip from '../components/BadgeWinnersStrip';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  completed: '#10b981',
  inProgress: '#3b82f6',
  inReview: '#8b5cf6',
  pending: '#f59e0b',
  danger: '#ef4444',
  warning: '#f59e0b',
  success: '#10b981'
};

// Öncelik renkleri (TaskBoard ile aynı)
const PRIORITIES = [
  { value: 'low', label: 'Düşük', hexColor: '#22c55e' }, // Yeşil
  { value: 'medium', label: 'Orta', hexColor: '#eab308' }, // Sarı
  { value: 'high', label: 'Yüksek', hexColor: '#f97316' }, // Turuncu
  { value: 'urgent', label: 'Acil', hexColor: '#ef4444' }, // Kırmızı
];

// Simple Line Chart Component
function SimpleLineChart({ data, title, subtitle }) {
  const [isVisible, setIsVisible] = useState(false);
  const chartRef = useRef(null);
  const width = 1200;
  const height = 200;
  const padding = 60;
  const topPadding = 20;
  const leftPadding = 80;
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (chartRef.current) observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);
  
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height: '250px' }}>
        <p className="text-gray-400">Veri yok</p>
      </div>
    );
  }
  
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const yAxisSteps = Math.min(Math.ceil(maxValue / 3), 4);
  
  const points = data.map((d, i) => {
    const x = data.length === 1 
      ? leftPadding + (width - leftPadding - padding) / 2
      : leftPadding + (i / (data.length - 1)) * (width - leftPadding - padding);
    const y = topPadding + (1 - d.value / maxValue) * (height - topPadding - padding);
    return `${x},${y}`;
  }).join(' ');

  return (
    <div ref={chartRef} className="glass rounded-2xl p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="w-full overflow-hidden">
        <svg width="100%" height="250" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" className="overflow-visible">
          <style>{`
            @keyframes drawLine { from { stroke-dashoffset: 3000; } to { stroke-dashoffset: 0; } }
            @keyframes fadeInArea { from { opacity: 0; } to { opacity: 1; } }
            @keyframes popIn { 0% { r: 0; opacity: 0; } 60% { r: 7; } 100% { r: 5; opacity: 1; } }
            .chart-line { stroke-dasharray: 3000; stroke-dashoffset: 3000; animation: drawLine 1.8s ease-out forwards; }
            .chart-area { opacity: 0; animation: fadeInArea 1s ease-out 0.8s forwards; }
            .chart-point { r: 0; opacity: 0; }
          `}</style>
          {Array.from({ length: yAxisSteps + 1 }).map((_, i) => {
            const y = topPadding + i * (height - topPadding - padding) / yAxisSteps;
            const value = Math.round(maxValue * (1 - i / yAxisSteps));
            return (
              <g key={`h-${i}`}>
                <line x1={leftPadding} y1={y} x2={width - padding} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                <text x={leftPadding - 15} y={y + 5} textAnchor="end" fontSize="12" fill="#6b7280" fontWeight="500">{value}</text>
              </g>
            );
          })}
          {data.map((d, i) => {
            const x = data.length === 1 
              ? leftPadding + (width - leftPadding - padding) / 2
              : leftPadding + (i / (data.length - 1)) * (width - leftPadding - padding);
            return (
              <line key={`v-${i}`} x1={x} y1={topPadding} x2={x} y2={height - padding} stroke="#f3f4f6" strokeWidth="1" strokeDasharray="3,3" />
            );
          })}
          {isVisible && (
            <>
              <polygon className="chart-area" fill={`${COLORS.success}20`} points={`${leftPadding},${height - padding} ${points} ${width - padding},${height - padding}`} />
              <polyline className="chart-line" fill="none" stroke={COLORS.success} strokeWidth="3" points={points} strokeLinecap="round" strokeLinejoin="round" />
              {data.map((d, i) => {
                const x = data.length === 1 
                  ? leftPadding + (width - leftPadding - padding) / 2
                  : leftPadding + (i / (data.length - 1)) * (width - leftPadding - padding);
                const y = topPadding + (1 - d.value / maxValue) * (height - topPadding - padding);
                return (
                  <g key={i}>
                    <circle cx={x} cy={y} r="5" fill={COLORS.success} stroke="white" strokeWidth="2" style={{ animation: `popIn 0.4s ease-out ${0.8 + i * 0.1}s forwards` }} className="chart-point" />
                    <title>{`${d.label}: ${d.value} görev`}</title>
                    <text x={x} y={height - 10} textAnchor="middle" fontSize="11" fill="#6b7280">{d.label}</text>
                  </g>
                );
              })}
            </>
          )}
        </svg>
      </div>
    </div>
  );
}

// Simple Doughnut Chart Component
function SimpleDoughnutChart({ data, title, subtitle }) {
  const [isVisible, setIsVisible] = useState(false);
  const chartRef = useRef(null);
  const size = 200;
  const center = size / 2;
  const radius = 70;
  const innerRadius = 50;
  
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (chartRef.current) observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);
  
  if (total === 0) {
    return (
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center justify-center" style={{ height: '250px' }}>
          <p className="text-gray-400">Veri yok</p>
        </div>
      </div>
    );
  }
  
  // Her segment için stroke-dasharray animasyonu hesapla
  const circumference = 2 * Math.PI * radius;
  
  return (
    <div ref={chartRef} className="glass rounded-2xl p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 px-4 min-h-[280px]">
        <div className="w-full max-w-[200px] aspect-square flex items-center justify-center">
          <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`} className="max-w-full max-h-full" preserveAspectRatio="xMidYMid meet">
            <style>{`
              @keyframes doughnutSpin { from { transform: rotate(-90deg); opacity: 0; } 20% { opacity: 1; } to { transform: rotate(270deg); opacity: 1; } }
            `}</style>
            {isVisible && data.map((item, index) => {
              const previousAngles = data.slice(0, index).reduce((sum, prevItem) => sum + ((prevItem.value / total) * 360), 0);
              const startCumulativeAngle = -90 + previousAngles;
              const percentage = (item.value / total) * 100;
              let angle = (percentage / 100) * 360;
              
              if (angle >= 359.99) {
                const firstHalfPath = [
                  `M ${center} ${center - radius}`, `A ${radius} ${radius} 0 0 1 ${center} ${center + radius}`,
                  `L ${center} ${center + innerRadius}`, `A ${innerRadius} ${innerRadius} 0 0 0 ${center} ${center - innerRadius}`, 'Z'
                ].join(' ');
                const secondHalfPath = [
                  `M ${center} ${center + radius}`, `A ${radius} ${radius} 0 0 1 ${center} ${center - radius}`,
                  `L ${center} ${center - innerRadius}`, `A ${innerRadius} ${innerRadius} 0 0 0 ${center} ${center + innerRadius}`, 'Z'
                ].join(' ');
                return (
                  <g key={index} style={{ opacity: 0, animation: `fadeInArea 0.6s ease-out ${index * 0.15}s forwards` }}>
                    <path d={firstHalfPath} fill={item.color} />
                    <path d={secondHalfPath} fill={item.color} />
                  </g>
                );
              }
              
              const startAngle = (startCumulativeAngle * Math.PI) / 180;
              const endAngle = ((startCumulativeAngle + angle) * Math.PI) / 180;
              const x1 = center + radius * Math.cos(startAngle);
              const y1 = center + radius * Math.sin(startAngle);
              const x2 = center + radius * Math.cos(endAngle);
              const y2 = center + radius * Math.sin(endAngle);
              const x3 = center + innerRadius * Math.cos(endAngle);
              const y3 = center + innerRadius * Math.sin(endAngle);
              const x4 = center + innerRadius * Math.cos(startAngle);
              const y4 = center + innerRadius * Math.sin(startAngle);
              const largeArc = angle > 180 ? 1 : 0;
              const pathData = [
                `M ${x1} ${y1}`, `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
                `L ${x3} ${y3}`, `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}`, 'Z'
              ].join(' ');
              
              return <path key={index} d={pathData} fill={item.color} style={{ opacity: 0, animation: `fadeInArea 0.5s ease-out ${0.2 + index * 0.15}s forwards` }} />;
            })}
            <circle cx={center} cy={center} r={innerRadius - 2} fill="white" />
            <text x={center} y={center + 6} textAnchor="middle" fontSize="20" fontWeight="bold" fill="#374151">{total}</text>
          </svg>
        </div>
        <div className="space-y-2 w-full md:w-auto flex flex-col items-center md:items-start">
          {data.map((item, index) => (
            <div key={index} className="flex items-center space-x-2" style={{ opacity: 0, animation: isVisible ? `fadeInArea 0.4s ease-out ${0.5 + index * 0.1}s forwards` : 'none' }}>
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-xs md:text-sm text-gray-700 whitespace-nowrap">{item.label}: {item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Simple Bar Chart Component
function SimpleBarChart({ data, title, subtitle }) {
  const [isVisible, setIsVisible] = useState(false);
  const chartRef = useRef(null);
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (chartRef.current) observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);
  
  if (maxValue === 0) {
    return (
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center justify-center min-h-[280px]">
          <p className="text-gray-400">Veri yok</p>
        </div>
      </div>
    );
  }
  
  return (
    <div ref={chartRef} className="glass rounded-2xl p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
          <AlertCircle className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-end justify-around gap-2 md:gap-8 px-2 md:px-12 min-h-[280px]">
        {data.map((item, index) => {
          const heightPercentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
          return (
            <div key={index} className="flex flex-col items-center flex-1 gap-2 min-w-0">
              <div className="w-full flex flex-col items-center justify-end" style={{ height: '240px' }}>
                <div className="text-xs md:text-sm font-semibold mb-1 transition-opacity duration-500" style={{ color: item.color, opacity: isVisible ? 1 : 0, transitionDelay: `${0.6 + index * 0.15}s` }}>
                  {item.value}
                </div>
                <div
                  className="w-full rounded-t-lg max-w-[60px] md:max-w-none"
                  style={{
                    height: isVisible && item.value > 0 ? `${heightPercentage * 0.7}%` : '0px',
                    backgroundColor: item.color,
                    minHeight: isVisible && item.value > 0 ? '20px' : '0px',
                    transition: `height 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.2 + index * 0.15}s, min-height 0.8s ease ${0.2 + index * 0.15}s`
                  }}
                />
              </div>
              <div className="text-[10px] md:text-sm font-medium text-gray-700 text-center break-all leading-tight">{item.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [showMobileMenu, setShowMobileMenu] = useState(null); // taskId veya null
  
  // Drawer state'leri (TaskBoard'dan)
  const { selectedTaskId, isOpen: showDrawer, drawerRef, openDrawer, closeDrawer: handleCloseDrawer } = useTaskDrawer();
  
  // Filtre state'leri
  const [showFilters, setShowFilters] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const [filterProject, setFilterProject] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [personalMode, setPersonalMode] = useState(false);
  
  // Filtre için gerekli veriler
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);

  // Admin "view as user" — TaskBoard ile aynı RBAC scope'unu kullansın
  const isAdmin = !!(
    user?.permissions?.includes('can_manage_users') &&
    user?.permissions?.includes('can_view_all_tasks')
  );

  // Push notification'dan gelen URL parameter kontrolü
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskIdParam = params.get('openTask');
    
    if (taskIdParam) {
      console.log('📬 [Push] Opening task from notification:', taskIdParam);
      openDrawer(taskIdParam);
      
      // URL'i temizle
      navigate('/', { replace: true });
    }
  }, [location.search]);

  // Service Worker'dan gelen mesajları dinle
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'NAVIGATE') {
        if (event.data.taskId) {
          console.log('📬 [Push] Opening task drawer:', event.data.taskId);
          openDrawer(event.data.taskId);
        }
      }
    };
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      };
    }
  }, []);

  useEffect(() => {
    // İlk yüklemede filtre verilerini çek
    fetchFilterData();
    fetchData();
    
    const interval = setInterval(() => {
      fetchData(true);
    }, 60000);
    
    return () => clearInterval(interval);
  }, [period, filterProject, filterPriority, filterAssignee, filterTag, customStartDate, customEndDate, personalMode]); // Filtreler değiştiğinde yeniden fetch
  
  const fetchFilterData = async () => {
    try {
      const [projectsRes, usersRes, tagsRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/reports/available-users`),
        axios.get(`${API}/tags`)
      ]);
      // ✅ Alfabetik sıralama
      const sortedProjects = projectsRes.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );
      const sortedUsers = usersRes.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );
      const sortedTags = tagsRes.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );
      setProjects(sortedProjects);
      setUsers(sortedUsers);
      setTags(sortedTags);
    } catch (error) {
      console.error('Failed to fetch filter data:', error);
    }
  };

  // Görev kartına tıklama handler'ı
  const handleTaskClick = (taskId) => {
    openDrawer(taskId);
  };

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMenu) {
        // Eğer tıklanan element dropdown veya 3 nokta butonu değilse kapat
        const isDropdown = event.target.closest('.dropdown-menu');
        const isButton = event.target.closest('.dropdown-button');
        
        if (!isDropdown && !isButton) {
          setShowMobileMenu(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileMenu]);

  // Görev taşıma fonksiyonu
  const handleMoveTask = async (taskId, newStatus) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}`, { status: newStatus });
      toast.success('Görev taşındı');
      setShowMobileMenu(null);
      fetchData(true); // Refresh data
    } catch (error) {
      console.error('Task move error:', error);
      toast.error('Görev taşınamadı');
    }
  };

  const fetchData = async (silent = false) => {
    if (period === 'custom' && (!customStartDate || !customEndDate)) return;
    try {
      if (!silent) setLoading(true);
      
      // Filtre parametrelerini hazırla
      const params = { period };
      if (filterProject !== 'all') params.projectId = filterProject;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterAssignee !== 'all') {
        // Admin: backend'den o kullanıcının full RBAC scope'unu çek (TaskBoard ile birebir)
        // Diğerleri: kişisel kapsam içinde kullanıcıya göre filtrele
        if (isAdmin) params.view_as = filterAssignee;
        else params.assignedTo = filterAssignee;
      }
      if (filterTag !== 'all') params.tagId = filterTag;
      if (personalMode && user?.id) params.currentlyAssignedTo = user.id;
      if (period === 'custom' && customStartDate) params.start_date = customStartDate.toISOString();
      if (period === 'custom' && customEndDate) params.end_date = new Date(customEndDate.getTime() + 86400000 - 1).toISOString();
      if (silent) params.nocache = Date.now();
      
      const statsRes = await axios.get(`${API}/stats`, { params });
      setStats(statsRes.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };
  
  const clearFilters = () => {
    setFilterProject('all');
    setFilterPriority('all');
    setFilterAssignee('all');
    setFilterTag('all');
    setPeriod('all');
    setCustomStartDate(null);
    setCustomEndDate(null);
  };
  
  const activeFilterCount = [
    filterProject !== 'all',
    filterPriority !== 'all',
    filterAssignee !== 'all',
    filterTag !== 'all',
  ].filter(Boolean).length;

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const periodLabels = {
    this_month: 'Bu Ay',
    prev_month: 'Önceki Ay',
    this_year: 'Bu Yıl',
    prev_year: 'Önceki Yıl',
    all: 'Tüm Zamanlar',
    custom: 'Özel Aralık'
  };

  const completionTrendData = stats?.completionTrend?.map(item => {
    let label;
    
    // Check date format and display accordingly
    if (item.date.length === 4) {
      // Year only (e.g., "2025")
      label = item.date;
    } else if (item.date.length === 7) {
      // Year-month format (e.g., "2025-10")
      const [year, month] = item.date.split('-');
      const monthNames = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      label = `${monthNames[parseInt(month) - 1]} ${year}`;
    } else {
      // Full date format (e.g., "2025-10-26")
      const date = new Date(item.date);
      label = `${date.getDate()}/${date.getMonth() + 1}`;
    }
    
    return {
      label,
      value: item.completed
    };
  }) || [];

  const statusChartData = [
    { label: 'Tamamlandı', value: stats?.completedTasks || 0, color: COLORS.completed },
    { label: 'İşlemde', value: stats?.inProgressTasks || 0, color: COLORS.inProgress },
    { label: 'İncelemede', value: stats?.inReviewTasks || 0, color: COLORS.inReview },
    { label: 'Beklemede', value: stats?.pendingTasks || 0, color: COLORS.pending }
  ];

  const priorityChartData = [
    { label: 'Acil', value: stats?.urgentPriority || 0, color: '#991b1b' }, // Koyu kırmızı - red-800
    { label: 'Yüksek', value: stats?.highPriority || 0, color: '#ea580c' }, // Turuncu - orange-600
    { label: 'Orta', value: stats?.mediumPriority || 0, color: '#eab308' }, // Sarı - yellow-500
    { label: 'Düşük', value: stats?.lowPriority || 0, color: '#22c55e' } // Yeşil - green-500
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-lg ${personalMode ? 'bg-gradient-to-br from-red-500 to-rose-600' : 'bg-gradient-to-br from-cyan-500 to-blue-500'}`}>
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            Dashboard{personalMode && user?.name ? ` (${user.name})` : ''}
          </h1>
          {/* Bir önceki ayın kategori birincileri + mansiyon (dönem seçiminden bağımsız) */}
          <BadgeWinnersStrip />
          <div className="flex items-center space-x-2 mt-2">
            <div className="flex items-center space-x-1 text-sm text-gray-500">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Canlı</span>
            </div>
            <span className="text-gray-400">•</span>
            <span className="text-sm text-gray-500">
              Son güncelleme: {lastUpdate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
        </div>
        
        {/* Buton grubu - mobilde wrap olacak */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          {/* Rozetler + Filtreler (mobilde 1. satır) | Tüm Zamanlar + Refresh (mobilde 2. satır sağa dayalı) */}
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => setShowBadges(!showBadges)}
              className={`relative flex-1 sm:flex-none ${showBadges ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}`}
              data-testid="dashboard-badges-toggle"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Rozetler
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              disabled={personalMode}
              className={`relative flex-1 sm:flex-none ${personalMode ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtreler
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-md w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto sm:ml-0">
            <div className={`flex-1 sm:flex-none ${personalMode ? 'opacity-50 pointer-events-none' : ''}`}>
            <DateRangeFilter
              period={period}
              onPeriodChange={setPeriod}
              startDate={customStartDate}
              endDate={customEndDate}
              onStartDateChange={setCustomStartDate}
              onEndDateChange={setCustomEndDate}
              showLabel={false}
              className="w-full"
            />
            </div>
            
            <button
              onClick={fetchData}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {/* Temizle Butonu - mobilde alt satıra geçer */}
          {activeFilterCount > 0 && (
            <Button variant="ghost" onClick={clearFilters} className="flex-shrink-0 w-full sm:w-auto">
              <X className="w-4 h-4 mr-2" />
              Temizle
            </Button>
          )}
        </div>
      </div>

      {/* Aylık Performans Rozetleri Duyurusu */}
      {showBadges && <BadgeAnnouncement />}

      {/* Filtre Paneli */}
      {showFilters && (
        <div className="glass rounded-xl p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-sm mb-2 block">Proje</Label>
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Projeler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Projeler</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Öncelik</Label>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Öncelikler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Öncelikler</SelectItem>
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Atanan Kişi</Label>
              <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Kullanıcılar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Kullanıcılar</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Kategori</Label>
              <Select value={filterTag} onValueChange={setFilterTag}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Kategoriler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Kategoriler</SelectItem>
                  {tags.map(tag => (
                    <SelectItem key={tag.id} value={tag.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {(() => {
          const filterParams = new URLSearchParams();
          if (filterProject !== 'all') filterParams.set('project', filterProject);
          if (filterPriority !== 'all') filterParams.set('priority', filterPriority);
          if (filterAssignee !== 'all') filterParams.set('assignee', filterAssignee);
          if (filterTag !== 'all') filterParams.set('tag', filterTag);
          if (period && period !== 'all') {
            filterParams.set('period', period);
            if (period === 'custom') {
              if (customStartDate) filterParams.set('start_date', customStartDate.toISOString().split('T')[0]);
              if (customEndDate) filterParams.set('end_date', customEndDate.toISOString().split('T')[0]);
            }
          }
          const qs = filterParams.toString();
          const suffix = qs ? `?${qs}` : '';
          return (
            <>
              <StatCard
                title="Toplam Görev"
                value={stats?.totalTasks || 0}
                icon={<Layers className="w-6 h-6" />}
                gradient="from-cyan-500 to-blue-500"
                linkTo={`/tasks${suffix}`}
              />
              <StatCard
                title="İşlemde"
                value={stats?.inProgressTasks || 0}
                icon={<Clock className="w-6 h-6" />}
                gradient="from-blue-400 to-blue-600"
                subtitle={stats?.totalTasks > 0 ? `%${Math.round((stats?.inProgressTasks || 0) / stats?.totalTasks * 100)} işlemde` : ''}
                linkTo={`/tasks${suffix ? suffix + '&' : '?'}status=işlemde`}
              />
              <StatCard
                title="İncelemede"
                value={stats?.inReviewTasks || 0}
                icon={<Target className="w-6 h-6" />}
                gradient="from-yellow-400 to-yellow-600"
                subtitle={stats?.totalTasks > 0 ? `%${Math.round((stats?.inReviewTasks || 0) / stats?.totalTasks * 100)} incelemede` : ''}
                linkTo={`/tasks${suffix ? suffix + '&' : '?'}status=incelemede`}
              />
              <StatCard
                title="Tamamlanan"
                value={stats?.completedTasks || 0}
                icon={<CheckCircle2 className="w-6 h-6" />}
                gradient="from-green-400 to-green-600"
                subtitle={`%${stats?.completionRate || 0} tamamlanma`}
                linkTo={`/tasks${suffix ? suffix + '&' : '?'}status=tamamlandı`}
              />
            </>
          );
        })()}
      </div>

      {/* Charts */}
      <div className="mb-4 sm:mb-6">
        {/* Üst Satır: Öncelik ve Durum Yan Yana */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div className="w-full">
            <SimpleBarChart
              data={priorityChartData}
              title="Öncelik Dağılımı"
              subtitle="Görevlerin öncelik durumu"
            />
          </div>
          
          <div className="w-full">
            <SimpleDoughnutChart
              data={statusChartData}
              title="Durum Dağılımı"
              subtitle="Görev durumları"
            />
          </div>
        </div>
        
        {/* Alt Satır: Tamamlanma Trendi Geniş */}
        <div className="w-full">
          <SimpleLineChart 
            data={completionTrendData}
            title="Tamamlanma Trendi"
            subtitle={periodLabels[period]}
          />
        </div>
      </div>

      {/* Task Detail Drawer */}
      <TaskDrawer
        selectedTaskId={selectedTaskId}
        isOpen={showDrawer}
        drawerRef={drawerRef}
        onClose={handleCloseDrawer}
        onDataChanged={() => {
          // Sessiz güncelleme - loading state tetiklemeden, cache bypass ile
          const params = { period, nocache: Date.now() };
          if (filterProject !== 'all') params.projectId = filterProject;
          if (filterPriority !== 'all') params.priority = filterPriority;
          if (filterAssignee !== 'all') {
            if (isAdmin) params.view_as = filterAssignee;
            else params.assignedTo = filterAssignee;
          }
          if (filterTag !== 'all') params.tagId = filterTag;
          if (personalMode && user?.id) params.currentlyAssignedTo = user.id;
          axios.get(`${API}/stats`, { params }).then(res => {
            setStats(res.data);
            setLastUpdate(new Date());
          }).catch(() => {});
        }}
      />
    </div>
  );
}

function StatCard({ title, value, icon, gradient, subtitle, linkTo }) {
  const content = (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-lg`}>
          <div className="text-white">{icon}</div>
        </div>
      </div>
      <h3 className="text-gray-600 text-sm font-medium mb-1">{title}</h3>
      <div className="text-3xl font-bold text-gray-900">
        <CountUp end={value} duration={1.5} />
      </div>
      {subtitle && (
        <div className="text-xs text-gray-500 mt-2">{subtitle}</div>
      )}
    </>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="glass rounded-2xl p-6 hover:shadow-lg transition-all duration-300 block cursor-pointer hover:scale-105">
        {content}
      </Link>
    );
  }

  return (
    <div className="glass rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
      {content}
    </div>
  );
}
